import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { MessageBarBody, SearchBox, Spinner, Switch, Text } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import * as ComponentFunctions from './ConceptSponsorForm.Functions';
import { useConceptSponsorFormStyles } from './ConceptSponsorForm.Styles';
import { GuideDrawer, GuideDrawerSection } from '../../../../../theme/components/GuideDrawer';
import { GuideMessageBar } from '../../../../../theme/components/GuideMessageBar';

export const ConceptSponsorFormComponent = (props: ComponentFunctions.Props) => {
  const { t } = useTranslation();
  const styles = useConceptSponsorFormStyles();

  const [searchText, setSearchText] = useState<string>('');
  const [infoMessage, setInfoMessage] = useState<string | undefined>();

  useEffect(() => {
    if (!props.isOpen) {
      return;
    }

    props.GetAllConcepts();
    props.GetAllConceptSponsors(props.sponsorId);
  }, [props.isOpen, props.GetAllConcepts, props.GetAllConceptSponsors, props.sponsorId]);

  useEffect(() => {
    if (!props.isOpen) {
      setSearchText('');
      setInfoMessage(undefined);
    }
  }, [props.isOpen]);

  const normalizedSearch = searchText.trim().toLowerCase();

  const associatedRows = useMemo(
    () =>
      props.rows.filter((row) => {
        if (!row.isAssociated) return false;
        if (!normalizedSearch) return true;
        return [row.conceptName, row.conceptReferenceId, row.conceptDescription]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      }),
    [props.rows, normalizedSearch]
  );

  const unassociatedRows = useMemo(
    () =>
      props.rows.filter((row) => {
        if (row.isAssociated) return false;
        if (!normalizedSearch) return true;
        return [row.conceptName, row.conceptReferenceId, row.conceptDescription]
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      }),
    [props.rows, normalizedSearch]
  );

  const handleToggleConcept = (rowKey: string, checked: boolean) => {
    const row = props.rows.find((item) => item.rowKey === rowKey);

    if (!row) {
      return;
    }

    setInfoMessage(undefined);

    if (checked && !row.isAssociated) {
      props.AddConceptSponsor({
        conceptId: row.conceptId,
        sponsorId: props.sponsorId
      });
      return;
    }

    if (!checked && row.isAssociated && row.associatedConceptSponsorId) {
      props.DeleteConceptSponsor(row.associatedConceptSponsorId);
    }
  };

  const isLoading = props.isLoadingConcepts || props.isLoadingConceptSponsors;

  return (
    <GuideDrawer
      open={props.isOpen}
      size="large"
      title={t('conceptSponsorForm.title', 'Seleccionar conceptos a asociar')}
      onDismiss={props.dismissPanel}
      closeLabel={t('conceptSponsorForm.actions.close', 'Cerrar')}
      secondaryAction={{ label: t('conceptSponsorForm.actions.cancel', 'Cancelar') }}
      primaryAction={{ label: t('conceptSponsorForm.actions.done', 'Listo'), onClick: props.dismissPanel }}
      testId="concept-sponsor-form-drawer"
    >
      <SearchBox
        placeholder={t('conceptSponsorForm.search.placeholder', 'Buscar por nombre, referencia o descripción')}
        value={searchText}
        onChange={(_, data) => setSearchText(data.value || '')}
      />

      {props.errorMessage && (
        <GuideMessageBar intent="error">
          <MessageBarBody>{props.errorMessage}</MessageBarBody>
        </GuideMessageBar>
      )}

      {infoMessage && (
        <GuideMessageBar intent="info">
          <MessageBarBody>{infoMessage}</MessageBarBody>
        </GuideMessageBar>
      )}

      {isLoading ? (
        <Spinner label={t('conceptSponsorForm.loading', 'Cargando conceptos...')} />
      ) : (
        <>
          {/* ── Conceptos asociados ── */}
          <GuideDrawerSection
            title={`${t('conceptSponsorForm.section.associated', 'Conceptos asociados')} (${associatedRows.length})`}
          >
            <div className={styles.listContainer}>
              {associatedRows.length === 0 ? (
                <Text className={styles.emptySection}>
                  {t('conceptSponsorForm.section.associatedEmpty', 'No hay conceptos asociados.')}
                </Text>
              ) : (
                associatedRows.map((row) => (
                  <div className={styles.listItem} key={row.rowKey}>
                    <Switch
                      checked={row.isAssociated}
                      onChange={(_, data) => handleToggleConcept(row.rowKey, Boolean(data.checked))}
                    />
                    <div className={styles.itemText}>
                      <Text weight="bold">{row.conceptName}</Text>
                      {row.conceptDescription && (
                        <Text className={styles.mutedText}>{row.conceptDescription}</Text>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </GuideDrawerSection>

          {/* ── Conceptos sin asociar ── */}
          <GuideDrawerSection
            title={`${t('conceptSponsorForm.section.unassociated', 'Conceptos sin asociar')} (${unassociatedRows.length})`}
          >
            <div className={styles.listContainerGrow}>
              {unassociatedRows.length === 0 ? (
                <Text className={styles.emptySection}>
                  {t('conceptSponsorForm.section.unassociatedEmpty', 'No hay conceptos sin asociar.')}
                </Text>
              ) : (
                unassociatedRows.map((row) => (
                  <div className={styles.listItem} key={row.rowKey}>
                    <Switch
                      checked={row.isAssociated}
                      onChange={(_, data) => handleToggleConcept(row.rowKey, Boolean(data.checked))}
                    />
                    <div className={styles.itemText}>
                      <Text weight="bold">{row.conceptName}</Text>
                      {row.conceptDescription && (
                        <Text className={styles.mutedText}>{row.conceptDescription}</Text>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </GuideDrawerSection>
        </>
      )}
    </GuideDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(ConceptSponsorFormComponent as any);
